'use client'

import React, { Children, isValidElement } from 'react'
import { motion } from 'motion/react'

import type { Easing, Transition, Variants } from 'motion/react'
import type { ReactNode } from 'react'

// ============================================================================
// Types and helpers
// ============================================================================

type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'none'

interface BaseRevealProps {
  ref?: React.RefObject<HTMLElement | null>
  children: ReactNode
  className?: string
  /** Animation direction - where the element comes from */
  direction?: RevealDirection
  /** Distance to travel in pixels */
  distance?: number
  /** Animation duration in seconds */
  duration?: number
  /** Delay before animation starts in seconds */
  delay?: number
  /** How much of the element should be visible before triggering (0-1) */
  threshold?: number
  /** Only animate once, or every time element enters view */
  once?: boolean
  /** Enable staggered children animation - each direct child animates in sequence */
  stagger?: boolean
  /** Stagger delay between children in seconds */
  staggerDelay?: number
  /** Disable animation entirely (useful for conditional animation) */
  disabled?: boolean
  /** Motion viewport options (e.g. margin). Merged with once/amount from threshold. */
  viewport?: { margin?: string; amount?: number; once?: boolean; [key: string]: unknown }
  role?: string
  style?: React.CSSProperties
}

const getOffset = (direction: RevealDirection, distance: number) => {
  switch (direction) {
    case 'up':
      return { y: distance }
    case 'down':
      return { y: -distance }
    case 'left':
      return { x: distance }
    case 'right':
      return { x: -distance }
    case 'none':
    default:
      return {}
  }
}

const createContainerVariants = (staggerDelay: number): Variants => ({
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelay,
    },
  },
})

const createItemVariants = (direction: RevealDirection, distance: number): Variants => {
  const offset = getOffset(direction, distance)
  return {
    hidden: {
      opacity: 0,
      ...offset,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
    },
  }
}

// Shared defaults - typed correctly for Motion
const defaultStaggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
}

const defaultStaggerTransition: Transition = {
  duration: 0.5,
  ease: [0.25, 0.1, 0.25, 1] as Easing,
}

const defaultEase: Easing = [0.25, 0.1, 0.25, 1]

// ============================================================================
// RevealSection - Main component (renders as div)
// ============================================================================

/**
 * RevealSection - Default div-based reveal animation
 *
 * @example Basic usage
 * ```tsx
 * <RevealSection>
 *   <h2>This fades in and slides up on scroll</h2>
 * </RevealSection>
 * ```
 *
 * @example With staggered children (auto-wrapped)
 * ```tsx
 * <RevealSection stagger staggerDelay={0.1}>
 *   <Card />   // Each child auto-animates in sequence
 *   <Card />
 *   <Card />
 * </RevealSection>
 * ```
 *
 * @example Custom direction and timing
 * ```tsx
 * <RevealSection direction="left" distance={60} duration={0.8} delay={0.2}>
 *   <aside>Slides in from left</aside>
 * </RevealSection>
 * ```
 */
export function RevealSection({
  ref,
  children,
  className,
  direction = 'up',
  distance = 40,
  duration = 0.6,
  delay = 0,
  threshold = 0.1,
  once = false,
  stagger = false,
  staggerDelay = 0.1,
  disabled = false,
  role,
  style,
}: BaseRevealProps) {
  const transition: Transition = {
    duration,
    delay,
    ease: defaultEase,
  }

  if (disabled) {
    return <div className={className}>{children}</div>
  }

  if (stagger) {
    return (
      <motion.div
        ref={ref as React.RefObject<HTMLDivElement> | undefined}
        className={className}
        initial="hidden"
        whileInView="visible"
        viewport={{ once, amount: threshold }}
        variants={createContainerVariants(staggerDelay)}
        role={role}
        style={style}
      >
        {Children.map(children, (child, index) => {
          if (!isValidElement(child)) return child
          return (
            <motion.div
              key={child.key ?? index}
              variants={defaultStaggerItemVariants}
              transition={defaultStaggerTransition}
            >
              {child}
            </motion.div>
          )
        })}
      </motion.div>
    )
  }

  return (
    <motion.div
      ref={ref as React.RefObject<HTMLDivElement> | undefined}
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={transition}
      style={style}
    >
      {children}
    </motion.div>
  )
}

/** Reveal as <ul> element */
export function RevealUl({
  children,
  className,
  direction = 'up',
  distance = 40,
  duration = 0.6,
  delay = 0,
  threshold = 0.2,
  once = false,
  stagger = false,
  staggerDelay = 0.1,
  disabled = false,
}: BaseRevealProps) {
  const transition: Transition = { duration, delay, ease: defaultEase }

  if (disabled) return <ul className={className}>{children}</ul>

  if (stagger) {
    return (
      <motion.ul
        className={className}
        initial="hidden"
        whileInView="visible"
        viewport={{ once, amount: threshold }}
        variants={createContainerVariants(staggerDelay)}
      >
        {Children.map(children, (child, index) => {
          if (!isValidElement(child)) return child
          return (
            <motion.li
              key={child.key ?? index}
              variants={defaultStaggerItemVariants}
              transition={defaultStaggerTransition}
            >
              {child}
            </motion.li>
          )
        })}
      </motion.ul>
    )
  }

  return (
    <motion.ul
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={transition}
    >
      {children}
    </motion.ul>
  )
}

// ============================================================================
// Convenience text reveal components
// ============================================================================

type RevealTextProps = Omit<BaseRevealProps, 'stagger' | 'staggerDelay'>

/** Reveal as <h1> element */
export function RevealH1({
  children,
  className,
  direction = 'up',
  distance = 30,
  duration = 0.5,
  delay = 0,
  threshold = 0.2,
  once = false,
  disabled = false,
}: RevealTextProps) {
  if (disabled) return <h1 className={className}>{children}</h1>

  return (
    <motion.h1
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={{ duration, delay, ease: defaultEase }}
    >
      {children}
    </motion.h1>
  )
}

/** Reveal as <h2> element */
export function RevealH2({
  children,
  className,
  direction = 'up',
  distance = 30,
  duration = 0.5,
  delay = 0,
  threshold = 0.2,
  once = false,
  disabled = false,
}: RevealTextProps) {
  if (disabled) return <h2 className={className}>{children}</h2>

  return (
    <motion.h2
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={{ duration, delay, ease: defaultEase }}
    >
      {children}
    </motion.h2>
  )
}

/** Reveal as <h3> element */
export function RevealH3({
  children,
  className,
  direction = 'up',
  distance = 30,
  duration = 0.5,
  delay = 0,
  threshold = 0.2,
  once = false,
  disabled = false,
}: RevealTextProps) {
  if (disabled) return <h3 className={className}>{children}</h3>

  return (
    <motion.h3
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={{ duration, delay, ease: defaultEase }}
    >
      {children}
    </motion.h3>
  )
}

/** Reveal as <p> element */
export function RevealP({
  children,
  className,
  direction = 'up',
  distance = 30,
  duration = 0.5,
  delay = 0,
  threshold = 0.2,
  once = false,
  disabled = false,
}: RevealTextProps) {
  if (disabled) return <p className={className}>{children}</p>

  return (
    <motion.p
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: threshold }}
      variants={createItemVariants(direction, distance)}
      transition={{ duration, delay, ease: defaultEase }}
    >
      {children}
    </motion.p>
  )
}

// ============================================================================
// Simple fade (no translation)
// ============================================================================

/**
 * RevealFade - Simple fade without translation
 */
export function RevealFade({
  children,
  className,
  duration = 0.6,
  delay = 0,
  threshold = 0.2,
  once = false,
  disabled = false,
}: Omit<BaseRevealProps, 'direction' | 'distance' | 'stagger' | 'staggerDelay'>) {
  if (disabled) return <div className={className}>{children}</div>

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once, amount: threshold }}
      transition={{ duration, delay, ease: defaultEase }}
    >
      {children}
    </motion.div>
  )
}
